import React, { useCallback } from 'react';
import classes from './style.module.scss';
import Button from 'app/common/Button/index';
import useTranslation from 'app/hooks/useTranslation';
import useForm, { FormValues } from "~/app/modules/form/useForm";
import { IssueClosePayload, APIResponse, IssueDetails, Machine } from "~/types";
import { useStoreActions, useStoreState } from "~/store";
import { toast } from "react-toastify";
import { checkIsObjectPropertiesEqual } from '~/app/helpers/object';
import { notify } from '~/app/helpers/notify';
import { useParams } from "react-router-dom";
import useWebSocket from '~/app/hooks/useWebSocket';
import { IssueCloseTypes } from '~/app/data/statuses/issue';
import { numberToNorwayNumberHour } from '~/app/helpers/format';

export interface FooterProps {
    onClose: () => void
    issueDetails: IssueDetails
    defaultFormValues?: FormValues<IssueClosePayload>
    onSave?: (id: IssueDetails['id']) => void
}

export type FooterComponent = React.FC<FooterProps>;

const Footer: FooterComponent = ({ onClose, issueDetails, defaultFormValues, onSave }) => {
    const { t } = useTranslation('issue');
    const { id } = useParams<{ id: Machine['id'] }>();
    const { values: formValues, formEvents: { onSubmit }, formState } = useForm<IssueClosePayload>({ formName: 'closeIssue' });
    const MachineFetchAction = useStoreActions(actions => actions.machine.fetchMachine);
    const closeIssueAction = useStoreActions(actions => actions.issue.closeIssue);
    const issueUpdating = useStoreState(state => state.issue.issueUpdating);
    const isMachineOfflineAvailable = useStoreState(state => state.machines.isOfflineAvailable);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const { syncClosedIssue } = useWebSocket();
    const issueId = issueDetails.id;
    const departmentId = issueDetails.issueDepartmentId;
    const displayTypeInIssues = useStoreState(state => state.machine.displayTypeInIssues);
    const isIssueTypeRequired = useStoreState(state => state.machine.isIssueTypeRequired);
    const isHPCodeRequired = useStoreState(state => state.issue.isHPCodeRequired);
    const isWorkOrderFormActive = useStoreState(state => state.issue.isWorkOrderFormActive);
    const displayCustomer = useStoreState(state => state.issue.displayCustomer);
    const displayProject = useStoreState(state => state.issue.displayProject);
    const machine = useStoreState(state => state.machine.data);

    const saveClickHandler = useCallback((): void => {
        if (formValues.issueCloseType === IssueCloseTypes.NOTWRONG) { // Set default value 0 to skip validation when closing issue  
            !!machine.isOperatingTimeVisible && (formValues.operatingTime = 0);
            !!machine.isOperatingCountVisible && (formValues.operatingCount = 0);
            formValues.ordinaryHours = numberToNorwayNumberHour(0);
            formValues.overTime50 = undefined;
            formValues.overTime100 = undefined;
            formValues.sortingType = 0;
            formValues.hpCode = 0;
            formValues.workType = 0;
            formValues.costCategory = 0;
            formValues.customer = 0;
            formValues.project = 0;
        }

        onSubmit(({ formState: { invalid } }) => {
            if (invalid) {
                toast.error(t('error.required'));
                return;
            }
            const payload: IssueClosePayload = {
                ...formValues,
                ...(issueId && { issueId }),
            } as IssueClosePayload;

            closeIssueAction(payload).then(async (data: APIResponse) => {
                onClose();
                issueId && onSave?.(issueId);
                data.success ? notify(t(`success.closed`), 'success') : notify(t(`error.${data?.msg}`), 'error');

                if (netWorkStatus === 0) {
                    syncClosedIssue({ issueId: issueId, departmentId: departmentId });
                }

                await MachineFetchAction(id ?? issueDetails?.machineId);
                (netWorkStatus === 0 && isMachineOfflineAvailable(id ?? issueDetails?.machineId));
            });
        })();
    }, [formValues, onSubmit, machine.isOperatingTimeVisible, machine.isOperatingCountVisible, issueId, closeIssueAction, t, onClose, onSave, netWorkStatus, MachineFetchAction, id, issueDetails?.machineId, isMachineOfflineAvailable, syncClosedIssue, departmentId]);

    const isDisabled = (): boolean => {
        let isDisabled = true;

        if (
            formValues.issueCloseType &&
            formValues.comment &&
            (
                !(isWorkOrderFormActive &&
                    (
                        (!!machine.isOperatingTimeVisible && !!machine.operatingTimeAllowed && !formValues?.operatingTime) ||
                        (!!machine.isOperatingCountVisible && !!machine.operatingCountAllowed && !formValues?.operatingCount) ||
                        (displayTypeInIssues && isIssueTypeRequired && !formValues?.sortingType) ||
                        (isHPCodeRequired && !formValues?.hpCode) ||
                        (!formValues?.ordinaryHours) ||
                        (!formValues?.workType) ||
                        (!formValues?.costCategory) ||
                        (displayCustomer && !formValues?.customer) ||
                        (displayProject && !formValues?.project)
                    )
                )
            ) &&
            !formState.invalid
        ) {
            isDisabled = false;
        }
        if (issueId && formValues && defaultFormValues && !isDisabled) {
            const keysToCheck = [
                "issueCloseType",
                "comment",
                ...(
                    isWorkOrderFormActive ? [
                        'ordinaryHours',
                        'workType',
                        'costCategory',
                        ...(!!machine.isOperatingTimeVisible ? ['operatingTime'] : []),
                        ...(!!machine.isOperatingCountVisible ? ['operatingCount'] : []),
                        ...(displayCustomer ? ['customer'] : []),
                        ...(displayProject ? ['project'] : []),
                        ...(isHPCodeRequired ? ['hpCode'] : []),
                        ...(displayTypeInIssues ? ['sortingType'] : [])
                    ] : []
                )
            ];
            isDisabled = checkIsObjectPropertiesEqual(defaultFormValues, formValues, keysToCheck);
        }
        return isDisabled;
    }

    return (
        <div className={`${classes['footer']}`}>
            <Button
                className={classes['abort']}
                onClick={onClose}>{t('cancel')}</Button>
            <Button
                className={classes['submit']}
                disabled={isDisabled()}
                loading={issueUpdating}
                onClick={saveClickHandler}>{t('issueClose.closeTheIssue')}</Button>
        </div>
    );
};

export default Footer
